import { DocumentNode } from '@apollo/client'
import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  PerformanceGoalsCreateMetricGroupDocument,
  PerformanceGoalsCreateMetricGroupMetricFragment,
  PerformanceGoalsCreateMetricGroupMutationVariables,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { PublicSelectField } from 'modules/goals/components/PublicSelectField'
import { validateTitle } from 'modules/goals/lib/metricGroupValidations'
import { CycleSelectField } from 'modules/performance_cycles/components/CycleSelectField'

export const CreateMetricGroupModal: React.FC<{
  onClose: () => void
  onSubmit?: (metric: PerformanceGoalsCreateMetricGroupMetricFragment) => void
  defaultValues?: Partial<PerformanceGoalsCreateMetricGroupMutationVariables>
  refetchQueries?: DocumentNode[]
}> = ({ onClose, onSubmit, defaultValues, refetchQueries = [] }) => {
  const [update] = useMutation(PerformanceGoalsCreateMetricGroupDocument, {
    extractorFn: (data) => data.goalsMutations.createGoalsMetricGroup,
    refetchQueries,
    awaitRefetchQueries: true,
  })

  const form = useForm<PerformanceGoalsCreateMetricGroupMutationVariables>({
    defaultValues: {
      ...defaultValues,
      cycleIds: [],
      title: defaultValues?.title || '',
      description: defaultValues?.description || '',
      public: defaultValues?.public ?? false,
    },
    validate(values) {
      const errors: Partial<typeof values> = {}

      const titleError = validateTitle(values.title)
      if (titleError) {
        errors.title = titleError
      }

      return errors
    },
    onSubmit: async (values) => {
      const variables: PerformanceGoalsCreateMetricGroupMutationVariables = {
        ...values,
      }
      const result = await update({ variables })
      const handled = handleResult(result, () => null)
      if (handled.success) {
        const goalGroup = handled.data.metricGroup
        if (!goalGroup) throw new Error('No metric group returned')

        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        track(trackingEvents.goals.createGoal, {
          has_cycle: goalGroup.performanceCycles.length > 0,
        })
        onSubmit?.(goalGroup)
      }
      return handled
    },
  })

  return (
    <Modal
      title={i18n.t('performance_goals.create_metric_group_modal.title')}
      description={i18n.t('performance_goals.create_metric_group_modal.description')}
      onClose={onClose}
      illustration="analytics-graph-lines"
      onSubmit={() => form.handleSubmit()}
      footer={
        <>
          <Button onClick={() => onClose()} type="secondary" disabled={form.isSubmitting}>
            {i18n.t('performance_goals.create_metric_group_modal.actions.cancel')}
          </Button>
          <Button submit disabled={form.isSubmitting}>
            {i18n.t('performance_goals.create_metric_group_modal.actions.create')}
          </Button>
        </>
      }
    >
      <Box gap="s12">
        <TextField
          error={form.errors.title?.toString()}
          label={i18n.t('performance_goals.metric_group_fields.title.label')}
          value={form.values.title}
          onChange={(value) => form.setFieldValue('title', value)}
        />
        <TextArea
          label={i18n.t('performance_goals.metric_group_fields.description.label')}
          value={form.values.description?.toString()}
          onChange={(value) => form.setFieldValue('description', value)}
        />
        <CycleSelectField
          label={i18n.t('performance_goals.cycles.cycle_select_field.label')}
          blankLabel={i18n.t('performance_goals.cycles.cycle_select_field.blank_label')}
          placeholder={i18n.t('performance_goals.cycles.cycle_select_field.placeholder')}
          value={form.values.cycleIds ? form.values.cycleIds[0] : undefined}
          onChange={(value) => form.setFieldValue('cycleIds', value ? [value] : [])}
        />
        <PublicSelectField
          value={form.values.public ?? false}
          onChange={(value) => form.setFieldValue('public', value)}
        />
      </Box>
    </Modal>
  )
}
